import { zodResolver } from '@hookform/resolvers/zod';
import { Typography } from '@mui/joy';
import { ArrowBendUpLeft } from '@phosphor-icons/react';
import { useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import { z } from 'zod';

import { convertLinkToString } from '~/assets/utils/helpers';
import { Button } from '~/components/ui/button';
import { FieldGroup } from '~/components/ui/fieldset';
import {
  FilePicker,
  FilePickerDropzone,
  FilePickerList,
  FilePickerListItemProps,
  FilePickerName,
  FilePickerPreview,
} from '~/components/ui/file-picker';
import { FormController, useFormController } from '~/components/ui/form';
import { InputField } from '~/components/ui/input';
import { TextAreaField } from '~/components/ui/textarea';
import { useToastNotificationContext } from '~/hooks/global/alert/useToastNotification';
import { useWrappedMutation } from '~/services/api/rest-api';
import { getErrorMessage } from '~/services/api/rest-api-error';
import { TenantService } from '~/services/api/tenant/endpoints';
import { CustomErrorTypes } from '~/services/api/tenant/types';

const MAX_ATTACHEMNT_SIZE = 5;

const getErrorType = (
  error: string,
  page: string,
  errorList: CustomErrorTypes[],
): string => {
  let errorName = '';
  if (Array.isArray(errorList)) {
    if (error === '500') {
      errorName = 'Internal Server Error';
    } else if (error === '400') errorName = 'Bad request';
    else if (error === '403') errorName = 'Forbidden access';
    else if (error === '408') errorName = 'Request timeout';
    else if (error === '415') errorName = 'Unsupported media type';
    else if (error === '404') {
      if (page === '/customer/settings/security') errorName = 'User not found';
      else errorName = 'Page not found';
    } else errorName = 'Unknown Error';
    const errorId = errorList.find((item) => item.name === errorName)?.id;
    return errorId ? `${errorId}` : '';
  }
  return '';
};

const schema = z.object({
  subject: z.string().min(1),
  description: z.string().min(1),
  code: z.string().optional().nullable(),
  errorPage: z.string().optional().nullable(),
  files: z.array(
    z.instanceof(File).refine((fileItem) => {
      if (MAX_ATTACHEMNT_SIZE) {
        return true;
      }
      const size = fileItem.size;
      if (size === 0) return false;
      const sizeInKB = (size / 1024).toFixed(1);

      return Number(sizeInKB) <= MAX_ATTACHEMNT_SIZE;
    }, 'File too large'),
  ),
});
type FormFields = z.infer<typeof schema>;

interface SubmitTicketFormProps {
  defaultValues: Partial<FormFields>;
}

export default function SubmitTicketForm({
  defaultValues,
}: SubmitTicketFormProps) {
  const intl = useIntl();
  const navigate = useNavigate();
  const { createToastNotification } = useToastNotificationContext();
  const form = useForm<FormFields>({
    defaultValues: { ...defaultValues, files: [] },
    resolver: zodResolver(schema),
  });

  const convertToBase64 = (file: File) =>
    new Promise((resolve) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onloadend = () => {
        return resolve(reader.result);
      };
    });

  const submitTicket = useWrappedMutation(
    TenantService.endpoints.submitCustomerTicket.useMutation(),
  );
  const errorTypeQuery = TenantService.endpoints.getErrorTypes.useQuery();

  const onSubmitHandler = form.handleSubmit(async (data) => {
    if (errorTypeQuery.isSuccess) {
      const errorType = getErrorType(
        data.code ?? '',
        data.errorPage ?? '',
        errorTypeQuery.data,
      );
      await submitTicket.mutateAsync(
        {
          subject: data.subject,
          description: data.description,
          error_type: errorType,
          page: convertLinkToString(data.errorPage),
          files: await Promise.all(
            data.files.map((file) => convertToBase64(file)),
          ).then((contents) => {
            return contents.map((content, index) => {
              return {
                base64: typeof content === 'string' ? content : '',
                fileName: data.files[index].name,
                fileType: data.files[index].type,
              };
            });
          }),
        },
        {
          onSuccess() {
            createToastNotification(
              intl.formatMessage({
                defaultMessage: 'Ticket submitted.',
              }),
              'success',
            );
          },
          onError(error) {
            createToastNotification(getErrorMessage(error), 'error');
          },
        },
      );
    } else {
      createToastNotification('Something went wrong', 'error');
    }
  });

  return (
    <div>
      <form onSubmit={onSubmitHandler}>
        <FieldGroup>
          <InputField
            control={form.control}
            field='subject'
            label={intl.formatMessage({ defaultMessage: 'Subject' })}
            required
            size='lg'
            placeholder={intl.formatMessage({
              defaultMessage: 'e.g. Record not found.',
            })}
          />

          <TextAreaField
            control={form.control}
            field='description'
            label={intl.formatMessage({ defaultMessage: 'Description' })}
            required
            size='lg'
            minRows={10}
            maxRows={15}
            placeholder={intl.formatMessage({
              defaultMessage: 'Kindly describe the issue',
            })}
          />
          <FormController control={form.control} field='files'>
            <FilePickerField />
          </FormController>
          <FieldGroup layout='grid'>
            <Button
              variant='plain'
              color='neutral'
              size='lg'
              startDecorator={<ArrowBendUpLeft size={20} />}
              disabled={submitTicket.state.isLoading}
              onClick={() => navigate(-1)}
            >
              <FormattedMessage defaultMessage='Return to home' />
            </Button>
            <Button
              variant='outlined'
              color='gradient-primary'
              shape='pill'
              size='lg'
              type='submit'
              disabled={submitTicket.state.isLoading}
            >
              <FormattedMessage defaultMessage='Submit' />
            </Button>
          </FieldGroup>
        </FieldGroup>
      </form>
    </div>
  );
}

const FilePickerField = () => {
  const controller = useFormController();

  const processFiles = async (files: File[]) => {
    controller?.field.onChange(files);
    // files.map(file => {
    // });
  };

  return (
    <FilePicker
      maxFiles={Infinity}
      onFileAccept={(details) => processFiles(details.files)}
    >
      <div className='grid gap-2'>
        <FilePickerDropzone />
        <FilePickerList>
          {(item) => <FileForUpload item={item} />}
        </FilePickerList>
      </div>
    </FilePicker>
  );
};

function FileForUpload({ item }: FilePickerListItemProps) {
  return (
    <div className='grid gap-2 p-2'>
      <div className='flex items-center gap-2'>
        <div className='w-12 shrink-0 flex items-center justify-center'>
          <FilePickerPreview file={item.file} />
        </div>
        <div className='max-w-64 w-full flex flex-col space-y-0.5'>
          <Typography level='body-sm'>
            <FilePickerName file={item.file} className='truncate min-w-0'>
              {item.file.name}
            </FilePickerName>
            <div>{item.getFileSize(item.file)}</div>
          </Typography>
        </div>
        <div className='ml-auto'>
          <Button
            size='sm'
            variant='plain'
            color='danger'
            onClick={() => item.deleteFile(item.file)}
          >
            <FormattedMessage defaultMessage='Delete File' />
          </Button>
        </div>
      </div>
    </div>
  );
}
